import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { CropChip } from '@/components/ui/crop-chip';
import type { VirtualColumn } from '@/components/ui/virtual-table';
import { resolveSowingDisplay } from '@/lib/sowings/seasonDisplay';

export interface FieldItem {
  id: string;
  name: string;
  number?: string | null;
  farm_id: string;
  farm_name?: string | null;
  brigade_id?: string | null;
  brigade_name?: string | null;
  cluster_id?: string | null;
  cluster_name?: string | null;
  area_ha?: number | null;
  soil_type?: string | null;
  has_irrigation: boolean;
  current_crop?: string | null;
  current_sowing_status?: string | null;
  current_season_year?: number | null;
  // AK-208: средний уклон, ° — приходит только при ?include=terrain. null у
  // полей без посчитанного рельефа.
  slope_mean_deg?: number | null;
  // Приходят в режиме архива (`archived_only=true` / `include_archived=true`,
  // AK-347): когда поле убрали и с какой причиной. У живых полей — null.
  archived_at?: string | null;
  archive_reason?: string | null;
}

export const ALL_COLUMN_KEYS = [
  'name',
  'number',
  'cluster',
  'farm',
  'brigade',
  'crop',
  'sowing_status',
  'area',
  'slope',
  'irrigation',
  'soil',
] as const;

export type ColKey = (typeof ALL_COLUMN_KEYS)[number];

export const COL_LABELS: Record<ColKey, string> = {
  name: 'Название',
  number: '№',
  cluster: 'Кластер',
  farm: 'Хозяйство',
  brigade: 'Отделение',
  crop: 'Культура',
  sowing_status: 'Статус посева',
  area: 'Площадь, га',
  slope: 'Уклон ср., °',
  irrigation: 'Орошение',
  soil: 'Тип почвы',
};

export const DEFAULT_VISIBLE: ColKey[] = [
  'name',
  'number',
  'cluster',
  'farm',
  'brigade',
  'crop',
  'sowing_status',
  'area',
  'slope',
  'irrigation',
];

const dash = <span className="text-muted-foreground">—</span>;

function widthFor(k: ColKey): string {
  switch (k) {
    case 'name':
      return 'minmax(180px, 1.4fr)';
    case 'number':
      return '80px';
    case 'area':
    case 'irrigation':
      return '120px';
    case 'slope':
      return '130px';
    default:
      return 'minmax(140px, 1fr)';
  }
}

/**
 * Столбцы полей для VirtualTable (AK-348). Раньше это был табличный компонент
 * с клиентской сортировкой и пагинацией — заменён на бесконечный список; сорт
 * на частично загруженном наборе вводил в заблуждение, поэтому убран.
 *
 * AK-208: колонка «Уклон ср., °» получила `sortKey='slope'` — сортировка
 * СЕРВЕРНАЯ (VirtualTable → onSortChange → перезапрос с ?sort=slope&order=…),
 * НЕ клиентский `.sort()` по загруженной порции. NULL'ы (поля без рельефа)
 * идут в конце (NULLS LAST на бэкенде).
 */
export interface FieldColumnsOptions {
  /**
   * Активный сезон линзы завершён (даты рамки прошли). Тогда занятие со
   * статусом «Запланировано» показывается как «Не реализован» — см.
   * `@/lib/sowings/seasonDisplay`. Данные при этом не меняются.
   */
  seasonCompleted?: boolean;
}

export function buildFieldColumns(
  visibleCols: ColKey[],
  opts: FieldColumnsOptions = {},
): VirtualColumn<FieldItem>[] {
  const byKey: Record<ColKey, VirtualColumn<FieldItem>> = {
    name: {
      key: 'name',
      header: COL_LABELS.name,
      width: widthFor('name'),
      // AK-750 — имя поля НАСТОЯЩАЯ ссылка, а не кнопка с navigate. Кнопкой
      // правый клик не даёт «Открыть в новой вкладке», а именно так founder
      // и разбирает список: держит список открытым, поля смотрит рядом.
      // Обычный клик тоже уводит в новую вкладку (`target`) — выборка полей,
      // на которую человек потратил несколько фильтров, не должна теряться
      // из-за одного взгляда в карточку. `rel` обязателен: без него новая
      // вкладка получает `window.opener` на список.
      // Подчёркивание постоянное, не `hover:underline`: что сюда можно
      // нажать, должно быть видно без наведения мыши (канон AK-743).
      cell: (r) => (
        <Link
          to="/fields/$fieldId"
          params={{ fieldId: r.id }}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Открыть карточку поля ${r.name} в новой вкладке`}
          className="block truncate rounded-sm text-primary underline underline-offset-2 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {r.name}
        </Link>
      ),
    },
    number: {
      key: 'number',
      header: COL_LABELS.number,
      width: widthFor('number'),
      cell: (r) => r.number ?? '—',
    },
    cluster: {
      key: 'cluster',
      header: COL_LABELS.cluster,
      width: widthFor('cluster'),
      cell: (r) => r.cluster_name ?? '—',
    },
    farm: {
      key: 'farm',
      header: COL_LABELS.farm,
      width: widthFor('farm'),
      cell: (r) => r.farm_name ?? '—',
    },
    brigade: {
      key: 'brigade',
      header: COL_LABELS.brigade,
      width: widthFor('brigade'),
      cell: (r) => r.brigade_name ?? '—',
    },
    crop: {
      key: 'crop',
      header: COL_LABELS.crop,
      width: widthFor('crop'),
      cell: (r) =>
        r.current_crop ? <CropChip name={r.current_crop} iconSize={16} /> : dash,
    },
    sowing_status: {
      key: 'sowing_status',
      header: COL_LABELS.sowing_status,
      width: widthFor('sowing_status'),
      cell: (r) => {
        const info = resolveSowingDisplay(
          r.current_sowing_status,
          opts.seasonCompleted,
        );
        return info ? (
          <Badge
            variant="secondary"
            className={info.className}
            title={info.hint ?? undefined}
          >
            {info.label}
          </Badge>
        ) : (
          dash
        );
      },
    },
    area: {
      key: 'area',
      header: COL_LABELS.area,
      width: widthFor('area'),
      align: 'right',
      cell: (r) => (r.area_ha != null ? Number(r.area_ha).toFixed(1) : '—'),
    },
    slope: {
      key: 'slope',
      header: COL_LABELS.slope,
      width: widthFor('slope'),
      align: 'right',
      sortKey: 'slope',
      cell: (r) =>
        r.slope_mean_deg != null ? (
          Number(r.slope_mean_deg).toFixed(1)
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    irrigation: {
      key: 'irrigation',
      header: COL_LABELS.irrigation,
      width: widthFor('irrigation'),
      cell: (r) =>
        r.has_irrigation ? (
          <Badge
            variant="secondary"
            className="bg-primary/10 text-primary hover:bg-primary/10"
          >
            есть
          </Badge>
        ) : (
          dash
        ),
    },
    soil: {
      key: 'soil',
      header: COL_LABELS.soil,
      width: widthFor('soil'),
      cell: (r) => r.soil_type ?? '—',
    },
  };

  return visibleCols
    .filter((k) => (ALL_COLUMN_KEYS as readonly string[]).includes(k))
    .map((k) => byKey[k]);
}
